import { normalizarData, normalizarValor } from '../../utils/index';

/**
 * Retorna TRUE se o array de dados se parecer com o Extrato Financeiro do iFood.
 */
export const detectar = (dadosArray) => {
    for (let i = 0; i < Math.min(dadosArray.length, 10); i++) {
        const linha = dadosArray[i];
        if (!linha || !Array.isArray(linha)) continue;
        const cleanRow = linha.map(c => String(c || '').trim().toLowerCase());
        
        if (
            cleanRow.includes('id_saldo') && 
            cleanRow.includes('valor_transacao') && 
            cleanRow.includes('data_repasse_esperada')
        ) {
            return true;
        }
    }
    return false;
};

/**
 * Processa a planilha bruta e extrai os repasses (lotes bancários),
 * deduplicando-os através da coluna id_saldo.
 * Lotes negativos (taxas, fretes) são vinculados ao lote positivo correto
 * via pedido_associado_ifood e subtraídos, gerando o valor líquido real
 * que o iFood deposita no banco.
 */
export const processar = (dadosArray) => {
    let idxIdSaldo = -1;
    let idxValorTransacao = -1;
    let idxData = -1;
    let idxCabecalho = -1;
    let idxPedido = -1;

    for (let i = 0; i < Math.min(dadosArray.length, 10); i++) {
        const linha = dadosArray[i];
        if (!linha || !Array.isArray(linha)) continue;
        
        const cleanRow = linha.map(c => String(c || '').trim().toLowerCase());
        
        const possibleIdSaldoIdx = cleanRow.indexOf('id_saldo');
        const possibleValorIdx = cleanRow.indexOf('valor_transacao');
        const possibleDataIdx = cleanRow.indexOf('data_repasse_esperada');
        const possiblePedidoIdx = cleanRow.indexOf('pedido_associado_ifood');

        if (possibleIdSaldoIdx !== -1 && possibleValorIdx !== -1 && possibleDataIdx !== -1) {
            idxCabecalho = i;
            idxIdSaldo = possibleIdSaldoIdx;
            idxValorTransacao = possibleValorIdx;
            idxData = possibleDataIdx;
            idxPedido = possiblePedidoIdx;
            break;
        }
    }

    if (idxCabecalho === -1) {
        throw new Error('Cabeçalho iFood não encontrado.');
    }

    // ===== PASSO 1: Catalogar todos os lotes (positivos e negativos) por id_saldo =====
    const lotesMap = {}; // { id_saldo: { valorTransacao, data, linhaIdx, pedidos: Set } }

    for (let i = idxCabecalho + 1; i < dadosArray.length; i++) {
        const linha = dadosArray[i];
        if (!linha || linha.length === 0) continue;

        const idSaldo = String(linha[idxIdSaldo] || '').trim();
        const valorRaw = String(linha[idxValorTransacao] || '').trim();
        const dataStr = String(linha[idxData] || '').trim();
        const pedido = idxPedido !== -1 ? String(linha[idxPedido] || '').trim() : '';

        if (!idSaldo || !valorRaw || !dataStr) continue;

        const valor = normalizarValor(valorRaw);
        if (valor === null) continue;

        if (!lotesMap[idSaldo]) {
            lotesMap[idSaldo] = {
                valorTransacao: valor,
                data: normalizarData(dataStr),
                dataRaw: dataStr,
                linhaIdx: i + 1,
                pedidos: new Set()
            };
        }

        // Coletar todos os pedidos associados a este lote
        if (pedido) {
            lotesMap[idSaldo].pedidos.add(pedido);
        }
    }

    // ===== PASSO 2: Separar lotes positivos e negativos =====
    const lotesPositivos = {};
    const lotesNegativos = {};

    for (const [idSaldo, lote] of Object.entries(lotesMap)) {
        if (lote.valorTransacao > 0 && lote.data) {
            lotesPositivos[idSaldo] = { ...lote, valorAjustado: lote.valorTransacao };
        } else if (lote.valorTransacao < 0 && lote.data) {
            lotesNegativos[idSaldo] = lote;
        }
    }

    // ===== PASSO 3: Vincular lotes negativos aos positivos via pedido_associado_ifood =====
    const orphans = []; // Taxas sem vínculo que precisam de decisão manual

    for (const [idNeg, loteNeg] of Object.entries(lotesNegativos)) {
        let vinculado = false;

        // Para cada pedido do lote negativo, procurar em qual lote positivo do mesmo dia ele aparece
        for (const pedidoNeg of loteNeg.pedidos) {
            if (!pedidoNeg) continue;

            for (const [idPos, lotePos] of Object.entries(lotesPositivos)) {
                // Mesmo dia + o pedido do lote negativo existe no lote positivo
                if (lotePos.data === loteNeg.data && lotePos.pedidos.has(pedidoNeg)) {
                    lotePos.valorAjustado += loteNeg.valorTransacao; // Soma valor negativo (subtrai)
                    vinculado = true;
                    break;
                }
            }
            if (vinculado) break;
        }

        // Se não encontrou vínculo por pedido, registrar como órfão para decisão manual
        if (!vinculado) {
            // Coletar os lotes positivos do mesmo dia como opções para o usuário
            const lotesDisponiveis = [];
            for (const [idPos, lotePos] of Object.entries(lotesPositivos)) {
                if (lotePos.data === loteNeg.data) {
                    lotesDisponiveis.push({
                        idSaldo: idPos,
                        valorOriginal: lotePos.valorTransacao,
                        valorAtual: Math.round(lotePos.valorAjustado * 100) / 100
                    });
                }
            }

            orphans.push({
                id: idNeg,
                data: loteNeg.data,
                valor: loteNeg.valorTransacao,
                descricao: 'Cobrança Avulsa iFood (ID: ' + idNeg + ')',
                lotesDisponiveis: lotesDisponiveis
            });
        }
    }

    // ===== PASSO 4: Gerar os repasses com valores líquidos =====
    const repasses = [];

    for (const [idSaldo, lote] of Object.entries(lotesPositivos)) {
        const valorFinal = Math.round(lote.valorAjustado * 100) / 100;
        if (valorFinal > 0) {
            repasses.push({
                linha: lote.linhaIdx,
                data: lote.data,
                valor: valorFinal,
                tipo: 'Lote iFood (ID: ' + idSaldo + ')',
                origem: 'IFood',
                idSaldo: idSaldo
            });
        }
    }

    // Se há órfãos, retornar objeto estruturado; senão, retornar array simples (compatibilidade)
    if (orphans.length > 0) {
        return { repasses, orphans };
    }

    return repasses;
};
